/**
 * Shared image generation utilities for cover images
 * Consolidates 40+ duplicate GeneratedImages implementations
 */
import * as htmlToImage from 'html-to-image';
import JSZip from 'jszip';

import { base64ToBlob, downloadZip } from '@/utils/helpers';

export interface ImageGenerationConfig {
  /** Pixel ratio for image quality (default: 1) */
  pixelRatio?: number;
  /** Image format (default: 'png') */
  format?: 'png' | 'jpg' | 'webp';
  /** JPG quality 0-1 (only for JPG format) */
  quality?: number;
  /** Skip cross-origin setup (default: false) */
  skipCrossOrigin?: boolean;
}

export interface ZipOptions {
  /** Filename for the ZIP file (default: 'generated-cover-images.zip') */
  filename?: string;
  /** Prefix for individual image files (default: 'image-') */
  imagePrefix?: string;
}

export interface ProgressTracker {
  /** Current progress count */
  current: number;
  /** Total items to process */
  total: number;
  /** Update progress callback */
  onProgress?: (current: number, total: number) => void;
}

/**
 * Sets crossOrigin attribute on external images within an element
 * Required for html-to-image to work with external images
 */
export function setCrossOriginForExternalImages(element: HTMLElement): void {
  const images = element.querySelectorAll('img');
  images.forEach((img) => {
    if (!img.src.startsWith(window.location.origin)) {
      img.crossOrigin = 'anonymous';
    }
  });
}

/**
 * Generates a PNG image from a DOM element
 */
export async function generatePngFromElement(
  element: HTMLElement,
  config: ImageGenerationConfig = {},
): Promise<string | undefined> {
  const pixelRatio = config.pixelRatio ?? 1;

  if (!config.skipCrossOrigin) {
    setCrossOriginForExternalImages(element);
  }

  try {
    const imageDataUrl = await htmlToImage.toPng(element, {
      pixelRatio,
    });
    return imageDataUrl;
  } catch (error) {
    console.error('Failed to generate image from element:', error);
    return undefined;
  }
}

/**
 * Generates a JPG image from a DOM element
 */
export async function generateJpgFromElement(
  element: HTMLElement,
  config: ImageGenerationConfig = {},
): Promise<string | undefined> {
  const pixelRatio = config.pixelRatio ?? 1;
  const quality = config.quality ?? 0.85;

  if (!config.skipCrossOrigin) {
    setCrossOriginForExternalImages(element);
  }

  try {
    const imageDataUrl = await htmlToImage.toJpeg(element, {
      pixelRatio,
      quality,
    });
    return imageDataUrl;
  } catch (error) {
    console.error('Failed to generate JPG from element:', error);
    return undefined;
  }
}

/**
 * Generates an image from a DOM element (auto-detects format)
 */
export async function generateImageFromElement(
  element: HTMLElement,
  config: ImageGenerationConfig = {},
): Promise<string | undefined> {
  const format = config.format ?? 'jpg';

  switch (format) {
    case 'jpg':
      return generateJpgFromElement(element, config);
    case 'png':
    default:
      return generatePngFromElement(element, config);
  }
}

/**
 * Creates a temporary holder element for image generation
 */
export function createImageHolder(
  className: string = 'generated-images-holder',
): HTMLDivElement {
  const holder = document.createElement('div');
  holder.classList.add(className);
  document.body.appendChild(holder);
  return holder;
}

/**
 * Removes a holder element from the DOM
 */
export function removeImageHolder(holder: HTMLElement): void {
  holder.remove();
}

/**
 * Generates multiple images from elements in parallel
 */
export async function generateImagesInParallel<T>(
  items: T[],
  generateFn: (item: T) => Promise<string | undefined>,
  onProgress?: (current: number, total: number) => void,
): Promise<string[]> {
  let completedCount = 0;
  const total = items.length;

  const promises = items.map(async (item) => {
    const result = await generateFn(item);
    completedCount++;
    if (onProgress) {
      onProgress(completedCount, total);
    }
    return result;
  });

  const results = await Promise.all(promises);
  return results.filter((url): url is string => url !== undefined);
}

/**
 * Generates multiple images from elements sequentially
 * Use this when parallel generation causes memory issues
 */
export async function generateImagesSequentially<T>(
  items: T[],
  generateFn: (item: T) => Promise<string | undefined>,
  onProgress?: (current: number, total: number) => void,
): Promise<string[]> {
  const results: string[] = [];
  const total = items.length;

  for (let i = 0; i < items.length; i++) {
    const result = await generateFn(items[i]);
    if (result !== undefined) {
      results.push(result);
    }
    if (onProgress) {
      onProgress(i + 1, total);
    }
  }

  return results;
}

/**
 * Creates a ZIP file from base64 image data URLs
 */
export async function createZipFromImages(
  imageDataUrls: string[],
  options: ZipOptions = {},
): Promise<Blob> {
  const zip = new JSZip();
  const imagePrefix = options.imagePrefix ?? 'image-';

  imageDataUrls.forEach((dataUrl, index) => {
    // Convert data URL to blob, replacing 'image/jpeg' with 'image/jpg'
    const modifiedDataUrl = dataUrl.replace(
      'data:image/jpeg;',
      'data:image/jpg;',
    );
    const blob = base64ToBlob(modifiedDataUrl);
    zip.file(`${imagePrefix}${index}.jpg`, blob, { binary: true });
  });

  return await zip.generateAsync({ type: 'blob' });
}

/**
 * Downloads images as a ZIP file
 */
export async function downloadImagesAsZip(
  imageDataUrls: string[],
  options: ZipOptions = {},
): Promise<void> {
  const filename = options.filename ?? 'generated-cover-images.zip';
  const zipBlob = await createZipFromImages(imageDataUrls, options);
  downloadZip(zipBlob, filename);
}

/**
 * Downloads selected images as a ZIP file
 */
export async function downloadSelectedImagesAsZip(
  allImages: (string | undefined)[],
  selectedIndices: number[],
  options: ZipOptions = {},
): Promise<void> {
  const filteredImages = allImages
    .filter((_, index) => selectedIndices.includes(index))
    .filter((img): img is string => img !== undefined);

  await downloadImagesAsZip(filteredImages, options);
}

/**
 * High-level image generation workflow
 * Handles holder creation, image generation, and cleanup
 */
export async function generateImagesWorkflow<T>(
  items: T[],
  buildElementFn: (item: T, holder: HTMLDivElement) => Promise<HTMLElement>,
  config: ImageGenerationConfig & {
    holderClassName?: string;
    sequential?: boolean;
    onProgress?: (current: number, total: number) => void;
  } = {},
): Promise<string[]> {
  const holder = createImageHolder(config.holderClassName);

  try {
    const generateFn = async (item: T) => {
      const element = await buildElementFn(item, holder);
      // IMPORTANT: Append element to holder so it's in the DOM for measurements
      holder.appendChild(element);
      const result = await generateImageFromElement(element, config);
      // Clean up after each generation to avoid memory issues
      holder.removeChild(element);
      return result;
    };

    const results = config.sequential
      ? await generateImagesSequentially(items, generateFn, config.onProgress)
      : await generateImagesInParallel(items, generateFn, config.onProgress);

    return results;
  } finally {
    removeImageHolder(holder);
  }
}

/**
 * Simplified image generation for single variation
 */
export async function generateSingleImage(
  buildElementFn: (
    holder: HTMLDivElement,
  ) => Promise<HTMLElement> | HTMLElement,
  config: ImageGenerationConfig & {
    holderClassName?: string;
    onAfterAppend?: (element: HTMLElement, holder: HTMLDivElement) => void;
  } = {},
): Promise<string | undefined> {
  const holder = createImageHolder(config.holderClassName);

  try {
    const element = await buildElementFn(holder);
    // IMPORTANT: Append element to holder so it's in the DOM for measurements
    holder.appendChild(element);

    // Allow for post-append calculations (e.g., dynamic padding based on measurements)
    if (config.onAfterAppend) {
      config.onAfterAppend(element, holder);
    }

    return await generateImageFromElement(element, config);
  } finally {
    removeImageHolder(holder);
  }
}

/**
 * Helper to add watermark image to a container
 */
export function addWatermark(
  container: HTMLElement,
  watermarkSrc: string,
  className: string,
  additionalClasses: string[] = [],
): HTMLImageElement {
  const watermark = new Image();
  watermark.classList.add(className);
  additionalClasses.forEach((cls) => watermark.classList.add(cls));
  watermark.src = watermarkSrc;
  container.appendChild(watermark);
  return watermark;
}

/**
 * Helper to create an image element with classes
 */
export function createImageElement(
  src: string,
  classes: string[],
): HTMLImageElement {
  const img = new Image();
  img.src = src;
  classes.forEach((cls) => img.classList.add(cls));
  return img;
}

/**
 * Helper to create a div element with classes
 */
export function createDivElement(classes: string[]): HTMLDivElement {
  const div = document.createElement('div');
  classes.forEach((cls) => div.classList.add(cls));
  return div;
}

/**
 * Helper to create a text element with content
 */
export function createTextElement(
  tag: 'div' | 'p' | 'span',
  content: string,
  classes: string[],
  isHtml: boolean = false,
): HTMLElement {
  const element = document.createElement(tag);
  classes.forEach((cls) => element.classList.add(cls));

  if (isHtml) {
    element.innerHTML = content;
  } else {
    element.innerText = content;
  }

  return element;
}
